import { cn } from '@/lib/utils';

export function Table({ className, ...props }: React.ComponentProps<'table'>) {
  // Separated borders, never collapsed. A collapsed border belongs to the table
  // grid rather than to the cell, and the grid stays behind when the header
  // sticks: rows then slide under a header with no hairline beneath it. With
  // `border-separate` each line is painted by the cell that declares it, so the
  // sticky header carries its own. `border-spacing-0` keeps the geometry
  // pixel-identical to the collapsed layout.
  return (
    <table
      className={cn(
        'w-full caption-bottom border-separate border-spacing-0 text-sm',
        className,
      )}
      {...props}
    />
  );
}

export function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
  return <thead className={cn('sticky top-0 z-10 bg-background', className)} {...props} />;
}

export function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
  return <tbody className={className} {...props} />;
}

export function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
  // Row groups cannot paint borders in the separated model — the rule goes on
  // the cells.
  return (
    <tfoot
      className={cn(
        'bg-muted/40 font-medium [&>tr>td]:border-t [&>tr>td]:border-border',
        className,
      )}
      {...props}
    />
  );
}

/**
 * A row's divider is declared here but painted by its cells, for the same
 * reason as the footer. Hide it on a single row with `[&>td]:border-b-0`.
 */
export function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
  return (
    <tr
      className={cn(
        'h-[52px] transition-colors duration-150 ease-out hover:bg-muted/50 data-[state=selected]:bg-muted',
        '[&>td]:border-b [&>td]:border-border/60',
        className,
      )}
      {...props}
    />
  );
}

export function TableHead({ className, ...props }: React.ComponentProps<'th'>) {
  return (
    <th
      className={cn(
        'h-10 whitespace-nowrap border-b border-border px-4 text-left align-middle text-xs font-normal text-muted-foreground',
        className,
      )}
      {...props}
    />
  );
}

export function TableCell({ className, ...props }: React.ComponentProps<'td'>) {
  return <td className={cn('px-4 align-middle', className)} {...props} />;
}

export function TableCaption({ className, ...props }: React.ComponentProps<'caption'>) {
  return <caption className={cn('mt-3 text-sm text-muted-foreground', className)} {...props} />;
}
